import { addNodeThunk } from "@common-reducers/ChatbotThunks";
import { useAppDispatch, useAppSelector } from "@common-reducers/hooks/store.hook";
import { setCurrentBot } from "@common/reducers/ChatbotReducer";
import { ArrowBack } from "@mui/icons-material";
import { Button } from "@mui/material";
import { Stack } from "@mui/system";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Node, useReactFlow } from "reactflow";
import { useTranslation } from "react-i18next";
import NodeButton from "./bot-components/node-button";
import classes from "./bt-chatbot.module.scss";
import { nodesInDev, NodeTypeDef, nodeTypes } from "./bt-nodes/bt-nodes-def";
import { AnalyticsService } from "@common/services/analytics-service";
import { reportAgentEvent } from "@common/services/agent-analytics";
import { namespace } from "@common/config/constants";


export default function ChatbotToolBar() {
  const { t } = useTranslation(namespace);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const analytics = useMemo(() => new AnalyticsService(), []);

  const isLocked = useAppSelector(state => {
    try {
      return state.ChatbotReducer?.isLocked ?? false;
    } catch (error) {
      return false;
    }
  });

  const reactFlowInstance = useReactFlow();
  const nodeTypesArray = Object.values(nodeTypes);

  const getNodeTypeTitle = useCallback((type: string) => {
    const def = nodeTypes[type as keyof typeof nodeTypes];
    return t(`chatbot.nodeTypes.${type}.title`, { defaultValue: def?.title || type });
  }, [t]);

  const getNodeTypeSecondaryTitle = useCallback((type: string) => {
    const def = nodeTypes[type as keyof typeof nodeTypes];
    return t(`chatbot.nodeTypes.${type}.secondaryTitle`, { defaultValue: def?.secondary_title || '' });
  }, [t]);


  const clickAddNode = useCallback((type: string) => {
    if (isLocked) {
      return;
    }

    const def = nodeTypes[type as keyof typeof nodeTypes];
    // Track node addition
    analytics.event('bot_node_added', {
      category: 'bot_building',
      action: 'add_node',
      node_type: type,
      node_title: def?.title || type,
      is_main_node: def?.isMainNode || false,
      add_method: 'toolbar_click'
    });
    // Unified builder-activity signal (bot_node_added also mirrors to the durable bus).
    reportAgentEvent('agent_builder_message', 'gemini', { kind: 'node_added', node_type: type });

    const addNodeWrapper = (node: Partial<Node>) => {
      reactFlowInstance.addNodes(node as Node);
    };

    // Get the current viewport directly from the reactFlowInstance
    const viewport = reactFlowInstance.getViewport();

    dispatch(addNodeThunk({
      type,
      addNodes: addNodeWrapper,
      fromToolbar: true,
      viewport: viewport // Pass the current viewport
    }));
  }, [isLocked, dispatch, reactFlowInstance, analytics]);

  const onDragStart = (event: React.DragEvent<HTMLDivElement>, nodeType: string, element: HTMLElement) => {
    if (isLocked) {
      event.preventDefault();
      return;
    }

    // Removed drag tracking - only track completed node additions, not drag starts

    // Set the node type as drag data
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.effectAllowed = 'move';

    // Create a clone of the actual button element
    const originalRect = element.getBoundingClientRect();
    const clone = element.cloneNode(true) as HTMLElement;

    // Style the clone to make it semi-transparent but preserve the original appearance
    Object.assign(clone.style, {
      position: 'fixed',
      top: '-9999px',
      left: '-9999px',
      width: `${originalRect.width}px`,
      height: `${originalRect.height}px`,
      opacity: '0.8',
      pointerEvents: 'none',
      userSelect: 'none',
      transform: 'translate(-50%, -50%)',
    });

    // Add the clone to the document body
    document.body.appendChild(clone);

    // Set the drag image and offset
    event.dataTransfer.setDragImage(clone, originalRect.width / 2, originalRect.height / 2);

    // Clean up the clone after dragging ends
    const cleanup = () => {
      if (clone && document.body.contains(clone)) {
        document.body.removeChild(clone);
      }
      
      window.removeEventListener('dragend', cleanup);
    };

    window.addEventListener('dragend', cleanup, { once: true });
  };

  // useEffect(() => {
  //   if (chatbotsLoaded && !onMountCheckExecuted.current) {
  //     onMountCheckExecuted.current = true;
  //     dispatch(setIsReadyToLoadFlow(true));
  //   }
  // }, [chatbotsLoaded, dispatch]);

  const handleBackToList = () => {
    // Track navigation back to list
    analytics.event('bot_editor_exit', {
      category: 'bot_navigation',
      action: 'back_to_list',
      exit_method: 'back_button'
    });
    
    dispatch(setCurrentBot(null));
    navigate('/bot-list-view');
  };

  return (
    <Stack className={classes.toolbar_wrapper}>
      <div className={classes.toolbar_scrollable_content}>
        {Object.values(nodeTypes)
          .filter(node => node.isMainNode)
          .map((node: NodeTypeDef, index) => {
            return (
              <div
                style={{
                  width: "100%",
                  cursor: isLocked ? 'not-allowed' : 'pointer',
                }}
                onClick={() => clickAddNode(node.type)}
                draggable={!isLocked}
                onDragStart={(event) => onDragStart(event, node.type, event.currentTarget)}
                key={index}
              >
                <NodeButton
                  bgColor={node.color}
                  title={getNodeTypeTitle(node.type)}
                  secondary_title={getNodeTypeSecondaryTitle(node.type)}
                  NodeIcon={node.nodeIcon}
                />
              </div>
            );
          })}

        <div style={{ display: 'flex', flexWrap: 'wrap', width: '100%' }}>
          {nodeTypesArray
            .filter(node => !node.isMainNode && !nodesInDev.includes(node.type))
            .map((node: NodeTypeDef, index) => {
              const isLastOddButton = (index === nodeTypesArray.length - 1) && (nodeTypesArray.length % 2 !== 0);

              return (
                <div
                  key={index}
                  onClick={() => clickAddNode(node.type)}
                  draggable={!isLocked}
                  onDragStart={(event) => onDragStart(event, node.type, event.currentTarget)}
                    style={{
                      flex: '0 0 50%',
                      boxSizing: 'border-box',
                      padding: '10px',
                      textAlign: isLastOddButton ? 'start' : 'center',
                      cursor: isLocked ? 'not-allowed' : 'pointer',
                    }}
                >
                  <NodeButton
                    bgColor={node.color}
                    title={getNodeTypeTitle(node.type)}
                    secondary_title={getNodeTypeSecondaryTitle(node.type)}
                    NodeIcon={node.nodeIcon}
                    isSecondaryNode={true}
                    svgPath={node.svgPath}
                  />
                </div>
              );
            })}
        </div>
      </div>

      <Button
        variant="outlined"
        startIcon={<ArrowBack sx={{ fontSize: 18 }} />}
        onClick={handleBackToList}
        sx={{
          mt: 'auto',
          mx: 2,
          mb: 2,
          alignSelf: 'stretch',
          height: 38,
          borderRadius: '8px',
          textTransform: 'none',
          fontSize: '13px',
          fontWeight: 500,
          letterSpacing: '0.01em',
          color: 'var(--bot-build-back-button, var(--color-text, #475569))',
          borderColor: 'var(--color-border, #e2e8f0)',
          backgroundColor: 'var(--color-surface, #ffffff)',
          boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
          transition: 'background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease, color 0.15s ease',
          '& .MuiButton-startIcon': {
            marginRight: '6px',
          },
          '&:hover': {
            bgcolor: 'var(--bot-build-back-button-hover-bg, var(--color-surface-muted, #f8fafc))',
            color: 'var(--bot-build-back-button-hover, var(--color-text, #1f2937))',
            borderColor: 'var(--color-text-muted, #94a3b8)',
            boxShadow: '0 2px 6px rgba(15, 23, 42, 0.08)',
          },
        }}
      >
        {t('chatbot.backToList')}
      </Button>
    </Stack>
  );
}
